/* globals document */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { intlShape } from 'react-intl';
import { locationShape } from 'react-router/lib/PropTypes';
import { autobind } from 'core-decorators';
import classNames from 'classnames';

import { byIdShape } from 'utils/propTypes';
import {
  fetchChargers as fetchChargersAction,
  chargerPlaces,
  chargerReviews,
  newChargerReview,
  chargerImages,
  newChargerImage,
  setChargerShowPlaceCategories,
} from 'actions';

import PageHeader from 'AppComponents/PageHeader';
import Address from 'AppComponents/Address';
import AverageRating from 'AppComponents/AverageRating';
import Reviews from 'AppComponents/Reviews';

import NotFound from 'AppViews/NotFound';

import ReviewForm from './components/ReviewForm';
import Review from './components/Review';
import Amenities from './components/Amenities';
import Places from './components/Places';
import Images from './components/Images';

import './styles.scss';


class Charger extends Component {

  static propTypes = {
    chargerImagesById: byIdShape.isRequired,
    chargerPlacesRequest: PropTypes.func.isRequired,
    chargersById: byIdShape.isRequired,
    isLoggedIn: PropTypes.bool,
    location: locationShape.isRequired,
    newChargerImage: PropTypes.func.isRequired,
    newChargerReview: PropTypes.func.isRequired,
    placesById: byIdShape.isRequired,
    reviewsById: byIdShape.isRequired,
    routeParams: PropTypes.shape({ chargerId: PropTypes.string }).isRequired,
    setChargerShowPlaceCategories: PropTypes.func.isRequired,
    showPlaceCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
    usersById: byIdShape.isRequired,
  };

  static defaultProps = {
    isLoggedIn: false,
  };

  static contextTypes = {
    intl: intlShape.isRequired,
    router: PropTypes.object.isRequired,
  };

  constructor(props, context) {
    super(props, context);
    this.state = {
      mobileShowMap: false,
    };
    if (typeof document !== 'undefined') {
      const htmlClassName = document.documentElement.className;
      const index = htmlClassName.indexOf('full-height');
      if (index !== -1) {
        document.documentElement.className = htmlClassName.replace(/\s?full-height/, '');
      }
    }
  }

  componentDidMount() {
    this.init();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.routeParams.chargerId !== this.props.routeParams.chargerId) {
      this.init(nextProps);
    }
  }

  @autobind
  setMobileShowMap(mobileShowMap) {
    this.setState({ mobileShowMap });
  }

  init(props) {
    const {
      routeParams, fetchChargers, chargerReviewsRequest, chargerImagesRequest,
    } = props || this.props;
    const chargerId = parseInt(routeParams.chargerId, 10);
    fetchChargers({ include: [chargerId] });
    chargerReviewsRequest(chargerId);
    chargerImagesRequest(chargerId);
  }

  @autobind
  newReviewSuccess(review) {
    this.props.newChargerReview(this.props.routeParams.chargerId, review);
  }

  mobileShowPlaces(props) {
    const { location } = props || this.props;
    return location.hash === '#places';
  }

  render() {
    const chargerId = this.props.routeParams.chargerId;
    const charger = this.props.chargersById[chargerId];

    if (!charger) return <NotFound objectType='Charger' />;

    const { isLoggedIn } = this.props;
    const { mobileShowMap } = this.state;

    const {
      title,
      address,
      chargers_text: chargersText,
      tesla_path: teslaPath,
      amenities,
      reviews,
      num_reviews: numReviews,
      description,
      user_review_status: userReviewStatus,
      average_rating: averageRating,
    } = charger;

    const descriptionLoc = [];
    if (address.city) descriptionLoc.push(address.city);
    if (address.state) {
      if (address.state.name) descriptionLoc.push(address.state.name);
      else if (address.state.code) descriptionLoc.push(address.state.code);
    }
    if (address.country.code !== 'US') descriptionLoc.push(address.country.name);

    const mobileShowPlaces = this.mobileShowPlaces();

    return (
      <div
        className={classNames('container', {
          'show-places': mobileShowPlaces,
          'with-map': mobileShowMap,
          'with-list': !mobileShowMap,
        })}
        styleName='charger-detail-page'
      >

        <Helmet
          title={title}
          meta={[
            { name: 'description', content: `View and share reviews, tips and comments about the ${title} electric vehicle charging station in ${descriptionLoc.join(', ')}.` }, // eslint-disable-line max-len
          ]}
        />

        <PageHeader title={title}>

          <small>

            <Address address={address} googleLink />

            <a
              target='_blank'
              rel='noopener noreferrer'
              href={`https://www.teslamotors.com/findus/location/supercharger${teslaPath}`}
            >
              View Tesla page &gt;
            </a>

          </small>

        </PageHeader>

        <section
          className={classNames(
            'charger-details',
            description ? 'has-summary' : 'no-summary',
            charger.images && charger.images.length > 0 ? 'has-images' : 'no-images',
          )}
          styleName='charger-details'
        >

          <div styleName='info'>
            { averageRating || description ?
              <div styleName='summary'>
                { averageRating ?
                  <AverageRating rating={averageRating} label='Average Rating' />
                : null }

                { description ?
                  <div
                    styleName='description'
                    dangerouslySetInnerHTML={{ __html: description }} // eslint-disable-line react/no-danger, max-len
                  />
                : null }
              </div>
            : null }

            <div styleName='details'>

              <div
                className='chargers'
                dangerouslySetInnerHTML={{ __html: chargersText }} // eslint-disable-line react/no-danger, max-len
              />

              <Amenities amenities={amenities} placesById={this.props.placesById} />

            </div>
          </div>

          <Images
            charger={charger}
            imagesById={this.props.chargerImagesById}
            usersById={this.props.usersById}
            onNewImageSuccess={this.props.newChargerImage}
            canUpload={isLoggedIn || false}
            styleName='charger-images'
          />

        </section>

        <Places
          charger={charger || { pk: chargerId }}
          chargerPlacesRequest={this.props.chargerPlacesRequest}
          location={this.props.location}
          showCategories={this.props.showPlaceCategories}
          setChargerShowPlaceCategories={this.props.setChargerShowPlaceCategories}
          placesById={this.props.placesById}
          mobileShowPlaces={mobileShowPlaces}
          mobileShowMap={mobileShowMap}
          setMobileShowMap={this.setMobileShowMap}
        />

        <Reviews
          canReview={userReviewStatus && userReviewStatus.can_review}
          isLoggedIn={isLoggedIn}
          loading={numReviews > 0 && reviews === undefined}
          numReviews={numReviews}
          reviewForm={(
            <ReviewForm
              charger={charger}
              newReviewSuccess={this.newReviewSuccess}
              reviewStatus={userReviewStatus || {}}
              numReviews={numReviews}
            />
          )}
        >
          { (reviews || []).map((id) => {
            const review = this.props.reviewsById[id];
            return (
              <Review
                key={id}
                review={review}
                user={this.props.usersById[review.user]}
              />
            );
          }) }
        </Reviews>

      </div>
    );
  }

}

const mapStateToProps = state => ({
  chargersById: state.chargers.byId,
  chargerImagesById: state.chargers.imagesById,
  placesById: state.places.byId,
  isLoggedIn: state.auth.isLoggedIn,
  usersById: state.users.byId,
  countries: state.chargers.countries,
  countriesById: state.chargers.countriesById,
  states: state.chargers.states,
  statesById: state.chargers.statesById,
  reviewsById: state.chargers.reviewsById,
  showPlaceCategories: state.chargers.showPlaceCategories,
});

const chargerPlacesRequest = chargerPlaces.request;
const chargerImagesRequest = chargerImages.request;
const chargerReviewsRequest = chargerReviews.request;

export default connect(
  mapStateToProps, {
    fetchChargers: fetchChargersAction,
    chargerPlacesRequest,
    chargerReviewsRequest,
    newChargerReview,
    chargerImagesRequest,
    newChargerImage,
    setChargerShowPlaceCategories,
  },
)(Charger);



// WEBPACK FOOTER //
// ./app/scripts/views/App/views/Charger/index.jsx